import { TabContext, TabList } from "@mui/lab";
import { Box, Stack, Tab, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import compInfos from "./servers-tabs.json";
import { useLangContext } from "../../../context/langContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { routerPaths } from "../../../constants/Paths";
import { dateFormatter } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import MuiTabPanel from "../../common/tab-panel/TabPanel";
import {
  PbxServerListColumnSortable,
  PbxServersListRequest,
} from "../../../services/Endpoints/pbxserver/PbxServerListEndpoint";
import DeleteAction from "../../common/delete-action/DeleteAction";
import EditAction from "../../common/edit-action/EditAction";
import { PbxServerDeleteRequest } from "../../../services/Endpoints/pbxserver/PbxServerDeleteEndpoint";
import { PbxServerSchema } from "../../../services/Schemas/PbxServer";
import AddNewServerModal from "./add-new-server-modal/AddNewServer";
import { useUrl } from "../../../hooks/urlEncoding";
import ViewActionLink from "../../common/view-action/ViewActionLink";
import { AccessEnums } from "../../../constants/accessEnum";
import { useCanAccess } from "../../../hooks/canAccess";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import { SortDirectionEnum } from "../../../services/Schemas/ListQueryString";
import useUrlSort from "../../../hooks/useUrlSort";
import MuiBaseModal from "../../common/modal/MuiModal";
import AddNewServerForm from "../../../forms/add-new-server/AddNewServerForm";
const infosData = {
  en: {
    title: {
      create: "افزودن سرور",
      update: "آپدیت سرور",
    },
    subTitle: {
      create: "جهت افزودن سرور فیلد هارو کامل کنید",
      update: "جهت آپدیت سرور فیلد هارو کامل کنید",
    },
  },
  fa: {
    title: {
      create: "افزودن سرور",
      update: "آپدیت سرور",
    },
    subTitle: {
      create: "جهت افزودن سرور فیلد هارو کامل کنید",
      update: "جهت آپدیت سرور فیلد هارو کامل کنید",
    },
  },
};

interface IBroadcast {
  operations: ReactNode;
  caption: string;
  pbx_driver: string;
  created_at: string;
  server_url: string | ReactNode;
  status?:  string;
  max_pending_calls: number;
  id: number;
}

export default function ServersTabsComponent() {
  const { parseQueryString, queryStringApply, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const { lang } = useLangContext();
  const { can } = useCanAccess();
  const location = useLocation();

  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [data, setData] = useState<IBroadcast[] | []>([]);
  const [waiting, setWaiting] = useState(false);
  const [reload, setReload] = useState(false);
  const { setNotification } = useNotificationContext();
  const [openModal, setOpenModal] = useState(false);
  const [serverInfo, setServerInfo] = useState<PbxServerSchema | undefined>(
    undefined,
  );
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const infos = compInfos[lang];
  const infosModal = infosData[lang];
  const protectedInstance = useProtectedAxiosInstance();

  const getData = async () => {
    try {
      const broadcastingListRequest = new PbxServersListRequest(
        protectedInstance,
      );
      setWaiting(true);
      const resp = await broadcastingListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      const broadcast = resp.data.pbx_servers.map((server) => ({
        created_at: server.created_at,
        max_pending_calls: server.max_pending_calls,
        status: server.status,
        caption: server.caption,
        pbx_driver: server.pbx_driver,
        server_url: (
          <Typography
            variant="footerRegular"
            component={Link}
            color={(theme) => theme.palette.others.link}
            sx={{
              textDecoration: "none",
            }}
            to={server.server_url}
          >
            {server.server_url}
          </Typography>
        ),
        id: server.id,
        operations: (
          <Stack direction="row" justifyContent="center" alignItems="center">
            <ViewActionLink
              disabled={!can(AccessEnums.AdminPbxServersDelete)}
              link={`/${routerPaths.SERVERS}/${server.id}`}
            />
            <EditAction
              disabled={!can(AccessEnums.AdminPbxServersUpdate)}
              handleEdit={() => handleUpdate(server)}
            />
            <DeleteAction
              disabled={!can(AccessEnums.AdminPbxServersDelete)}
              handleDelete={() => handleDelete(server.id)}
            />
          </Stack>
        ),
      }));
      setWaiting(false);
      setData(broadcast);
      setTotal(resp.data.pbx_servers.length);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [reload, page, value, rowsPerPage, location.search]);

  const handleDelete = async (id: number) => {
    const pbxServerDeleteRequest = new PbxServerDeleteRequest(
      protectedInstance,
    );
    setWaiting(true);
    try {
      const resp = await pbxServerDeleteRequest.send(id);
      await getData();
      setNotification({ type: "success", message: "سرور با موفقیت حذف شد." });
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  const handleUpdate = async (serverInfo: PbxServerSchema) => {
    setOpenModal(true);
    setServerInfo(serverInfo);
  };

  const tInfo = compInfos[lang];
  const colName = tInfo.columns;
  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "caption",
      label: colName.caption,
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "pbx_driver",
      label: colName.pbx_driver,
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "max_pending_calls",
      label: colName.max_pending_calls,
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "status",
      label: colName.status,
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "created_at",
      label: colName.created_at,
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "operations",
      label: colName.operations,
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };
  const handleReload = () => {
    setReload(!reload);
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setReload(!reload)}
        hasWaiting={waiting}
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={data}
        rowsPerPage={rowsPerPage}
        hasSearchFilter
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
      <MuiBaseModal
        onClose={() => setOpenModal(false)}
        open={openModal}
        title={!!serverInfo ? infosModal.title.update : infosModal.title.create}
        subTitle={!!serverInfo ? infosModal.subTitle.update : infosModal.subTitle.create}
        hasCloseIcon
      >
        <AddNewServerForm
          onClose={() => setOpenModal(false)}
          serverInfo={serverInfo}
          handleReload={handleReload}
        />
      </MuiBaseModal>
      <AddNewServerModal
        openModal={openModal}
        onClose={() => setOpenModal(false)}
        handleReload={handleReload}
        serverInfo={serverInfo}
      />
    </Box>
  );
}
